import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInboxRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNewRounded";
import { Button, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import HelpArticleLink from "@entrilia/shared/components/HelpArticleLink";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import StatusChip from "@entrilia/shared/components/StatusChip";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { convertISODate, convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { Contact, ContactPermissions } from "../../../../../../api/types/fundStructureTypes";
import { ContactStatus } from "../../../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../../../context/useClientContext";
import { intercomArticleIds } from "../../../../../../utilities/intercomArticles";
import EntitySection from "../../../../../entityFields/EntitySection";
import EntitySectionHeader from "../../../../../entityFields/EntitySectionHeader";
import SendInvitationDialog from "../../SendInvitationDialog";
import ContactEmailChangeDialog from "../ContactEmailChangeDialog";
import { getSignInEmailChangeTooltip } from "../contactEmailEditModel";
import LockedPortalAccessSection from "./LockedPortalAccessSection";
import PortalAccessContext from "./PortalAccessContext";
import {
  accessRolesExplanation,
  getAccessSummary,
  getActivityRow,
  getContactedRow,
  getDocumentActivityGate,
  getEmailDeliveryRow,
  getImpersonationGate,
  getInvitationGate,
  getMfaRow,
  getPasswordResetGate,
  getRegistrationByEmailRow,
  getRegistrationRow,
  getRelatedGuides,
  getSignInEmailRow,
  getSignInRow,
  isSignInRowVisible,
  manageAccessTooltip,
  mfaStateLabels,
  registrationByEmailExplanation,
  resumeDeliveryTooltip,
  viewEmailsTooltip,
} from "./portalAccessModel";
import PortalAccessRelatedGuides from "./PortalAccessRelatedGuides";
import PortalAccessRow from "./PortalAccessRow";
import PortalAccessSignInRow from "./PortalAccessSignInRow";
import ResetPasswordButton from "./ResetPasswordButton";
import ResetPasswordDialog from "./ResetPasswordDialog";
import ResumeDeliveryDialog from "./ResumeDeliveryDialog";

interface Props {
  contact: Contact;
  permissions: ContactPermissions[];
  hasPermissionsToManage: boolean;
  onManageAccess: () => void;
  onViewEmails: () => void;
  onViewDocumentActivity: () => void;
  onUpdateContactDetails: () => void;
}

const ContactPortalAccessSection = ({
  contact,
  permissions,
  hasPermissionsToManage,
  onManageAccess,
  onViewEmails,
  onViewDocumentActivity,
  onUpdateContactDetails,
}: Props) => {
  const { investorPortalApp, title: companyTitle, hasPermissions, hasAnyPermission } = useClientContext();
  const {
    portalAccess,
    error: portalAccessError,
    isLoading: isPortalAccessUnknown,
    refetch: refetchPortalAccess,
    emailEditMode,
  } = useDefinedContext(PortalAccessContext);

  const [openDialog, setOpenDialog] = useState<
    "reset_password" | "send_invitation" | "resume_delivery" | "change_email"
  >();
  const [isPasswordJustReset, setIsPasswordJustReset] = useState(false);

  const hasAccessToDocumentActivity = hasPermissions(["ViewInvestorPortalDocumentViewsAnalytics"]);
  const hasAccessToImpersonation = hasAnyPermission(["ViewInvestorPortalOnBehalfOfInvestor"]);

  const handleInvitationSent = () => {
    setOpenDialog((current) => (current === "send_invitation" ? undefined : current));
    refetchPortalAccess();
    onUpdateContactDetails();
  };

  const handleResetCompleted = () => {
    setIsPasswordJustReset(true);
    refetchPortalAccess();
  };

  const handleDeliveryResumed = () => {
    setOpenDialog(undefined);
    refetchPortalAccess();
  };

  const handleEmailChanged = () => {
    setOpenDialog(undefined);
    refetchPortalAccess();
    onUpdateContactDetails();
  };

  if (!investorPortalApp) {
    return <LockedPortalAccessSection subtitle={contact.investorPortalLogin} companyTitle={companyTitle} />;
  }

  const isRegistered = contact.status === ContactStatus.Registered;
  const impersonationGate = getImpersonationGate({
    hasEmail: Boolean(contact.email),
    hasPortalAccess: contact.hasAccessToInvestorPortal,
  });
  const invitationGate = getInvitationGate({
    hasEmail: Boolean(contact.email),
    hasPortalAccess: contact.hasAccessToInvestorPortal,
    suppressionState: portalAccess?.emailSuppressionState,
    isLoading: isPortalAccessUnknown,
  });

  const access = getAccessSummary(permissions);
  const contacted = getContactedRow({
    invitationSentAtText: contact.invitationSentAt ? convertISODateTime(contact.invitationSentAt) : undefined,
    lastEmailDelivery: portalAccess?.lastEmailDelivery,
    bouncedAtText: convertISODate(portalAccess?.lastEmailDelivery?.failedAt),
    hasEmail: Boolean(contact.email),
    isDeliveryStatusLoaded: portalAccess !== undefined,
    suppressionState: portalAccess?.emailSuppressionState,
  });

  const registration = getRegistrationRow({
    story: portalAccess?.registrationStory,
    isRegistered,
    hasPortalAccess: contact.hasAccessToInvestorPortal,
    registrationDateText: contact.registrationDate ? convertISODate(contact.registrationDate) : undefined,
    isStoryLoading: isPortalAccessUnknown,
  });
  const emailDelivery = getEmailDeliveryRow(portalAccess?.emailSuppressionState);
  const registrationByEmail = getRegistrationByEmailRow(
    portalAccess?.emailRegistrationStatus,
    portalAccess?.emailSuppressionState
  );
  const signIn = getSignInRow({
    signInState: portalAccess?.signInState,
    passwordReset: portalAccess?.passwordReset,
    occurredAtText: convertISODateTime(portalAccess?.signInState?.recentIssue?.occurredAt),
  });
  const activity = getActivityRow({
    lastActivityText: contact.lastActivity ? convertISODateTime(contact.lastActivity) : undefined,
    hasUndatedActivity: contact.hasUndatedActivity,
  });
  const mfa = getMfaRow({
    status: portalAccess?.mfaStatus,
    isStatusLoaded: portalAccess !== undefined,
    isRegistered,
  });

  const documentActivityGate = getDocumentActivityGate({
    hasDocumentActivity: portalAccess?.hasDocumentActivity,
    isLoading: isPortalAccessUnknown,
  });

  const passwordResetGate = getPasswordResetGate({
    passwordReset: portalAccess?.passwordReset,
    isGateUnknown: isPortalAccessUnknown,
    isAccountDisabled: portalAccess?.signInState?.accountStatus === "Disabled",
  });

  const signInEmail = getSignInEmailRow(contact.investorPortalLogin);
  const canChangeSignInEmail = emailEditMode.kind === "dialog";

  const relatedGuides = getRelatedGuides({
    isRegistered,
    isRegistrationByEmailRowShown: registrationByEmail !== undefined,
    isImpersonationPossible: impersonationGate.canImpersonate && hasAccessToImpersonation,
    isMfaRowShown: mfa !== undefined,
  });

  const isSignInRowShown = isSignInRowVisible(signIn, isPasswordJustReset);
  const isResetOfferedBySignInRow = isSignInRowShown && signIn.action === "resetPassword";

  return (
    <EntitySection>
      <EntitySectionHeader title="Portal Access" subtitle={contact.investorPortalLogin}>
        <HelpArticleLink
          articleId={intercomArticleIds.PORTAL_ACCESS_PANEL}
          underline="none"
          sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          <Typography component="span">Learn More</Typography>
          <OpenInNewIcon />
        </HelpArticleLink>
      </EntitySectionHeader>
      <Stack divider={<Divider />}>
        <Stack component="dl" divider={<Divider />} sx={{ m: 0 }}>
          <PortalAccessRow
            label="Access"
            value={access.value}
            caption={access.caption}
            help={access.investorNames.length > 0 ? accessRolesExplanation : undefined}
            helpArticleId={intercomArticleIds.MANAGE_PORTAL_USER_ACCESS}
            actions={
              <TooltipForDisabledAction title={manageAccessTooltip}>
                <Button variant="outlined" onClick={onManageAccess}>
                  Manage access
                </Button>
              </TooltipForDisabledAction>
            }
          >
            {access.investorNames.length > 0 && (
              <InlineItemsList
                items={access.investorNames}
                propGetter={(name) => name}
                displayCount={2}
                popupTitle="Investors"
              />
            )}
          </PortalAccessRow>
          <PortalAccessRow
            label="Contacted"
            value={contacted.value}
            caption={contacted.caption}
            help={contacted.help}
            helpDetails={contacted.helpDetails}
            captionStatus={contacted.captionStatus}
            actions={
              (contacted.hasDeliveryDetail || hasPermissionsToManage) && (
                <>
                  {contacted.hasDeliveryDetail && (
                    <TooltipForDisabledAction title={viewEmailsTooltip}>
                      <Button variant="outlined" startIcon={<MailOutlineRoundedIcon />} onClick={onViewEmails}>
                        View emails
                      </Button>
                    </TooltipForDisabledAction>
                  )}
                  {hasPermissionsToManage && (
                    <TooltipForDisabledAction title={invitationGate.tooltip} isActionDisabled={!invitationGate.canSend}>
                      <Button
                        variant="outlined"
                        startIcon={<ForwardToInboxIcon />}
                        disabled={!invitationGate.canSend}
                        onClick={() => setOpenDialog("send_invitation")}
                      >
                        {contact.invitationSentAt ? "Re-send invitation" : "Send invitation"}
                      </Button>
                    </TooltipForDisabledAction>
                  )}
                </>
              )
            }
          />
          {emailDelivery && (
            <PortalAccessRow
              label="Email delivery"
              value={emailDelivery.value}
              help={emailDelivery.help}
              status={emailDelivery.status}
              actions={
                emailDelivery.isBlocked &&
                hasPermissionsToManage && (
                  <TooltipForDisabledAction title={resumeDeliveryTooltip}>
                    <Button variant="outlined" onClick={() => setOpenDialog("resume_delivery")}>
                      Resume delivery
                    </Button>
                  </TooltipForDisabledAction>
                )
              }
            />
          )}
          <PortalAccessRow
            label="Registration"
            value={registration.value}
            caption={registration.caption}
            status={registration.status}
            help={registration.help}
            helpArticleId={intercomArticleIds.INVESTOR_PORTAL_FOR_LPS}
            actions={
              isResetOfferedBySignInRow ? undefined : (
                <ResetPasswordButton
                  gate={passwordResetGate}
                  hasPermissionsToManage={hasPermissionsToManage}
                  onClick={() => setOpenDialog("reset_password")}
                />
              )
            }
          >
            {isPortalAccessUnknown && (
              <Typography variant="caption">
                <Skeleton animation={false} width="60%" />
              </Typography>
            )}
          </PortalAccessRow>
          <PortalAccessRow
            label="Sign-in email"
            value={signInEmail.value}
            caption={signInEmail.caption}
            actions={
              hasPermissionsToManage && (
                <TooltipForDisabledAction
                  title={getSignInEmailChangeTooltip(emailEditMode)}
                  isActionDisabled={!canChangeSignInEmail}
                >
                  <Button
                    variant="outlined"
                    disabled={!canChangeSignInEmail}
                    onClick={() => setOpenDialog("change_email")}
                  >
                    Change
                  </Button>
                </TooltipForDisabledAction>
              )
            }
          />
          {registrationByEmail && (
            <PortalAccessRow
              label="Registration by email"
              value={registrationByEmail.value}
              caption={registrationByEmail.caption}
              help={registrationByEmailExplanation}
              helpArticleId={intercomArticleIds.REGISTRATION_BY_EMAIL}
            />
          )}
          {isSignInRowShown && (
            <PortalAccessSignInRow
              model={signIn}
              contactName={contact.name}
              hasPermissionsToManage={hasPermissionsToManage}
              passwordResetGate={passwordResetGate}
              onResetPassword={() => setOpenDialog("reset_password")}
            />
          )}
          <PortalAccessRow
            label={`Active at ${companyTitle}`}
            value={activity.value}
            help={activity.help}
            actions={
              hasAccessToDocumentActivity && (
                <TooltipForDisabledAction
                  title={documentActivityGate.tooltip}
                  isActionDisabled={!documentActivityGate.canView}
                >
                  <Button
                    variant="outlined"
                    startIcon={<DescriptionOutlinedIcon />}
                    disabled={!documentActivityGate.canView}
                    onClick={onViewDocumentActivity}
                  >
                    Document activity
                  </Button>
                </TooltipForDisabledAction>
              )
            }
          />
          {mfa ? (
            <PortalAccessRow
              label="MFA"
              value={
                mfa.state === "Unknown" ? (
                  mfaStateLabels.Unknown
                ) : (
                  <StatusChip
                    label={mfaStateLabels[mfa.state]}
                    withDot
                    color={(t) => (mfa.state === "On" ? t.palette.success.main : t.palette.text.secondary)}
                  />
                )
              }
              caption={mfa.caption}
              help={mfa.help}
              helpArticleId={intercomArticleIds.MFA}
            />
          ) : (
            isPortalAccessUnknown &&
            contact.status === ContactStatus.Registered && (
              <PortalAccessRow
                label="MFA"
                value={<Skeleton animation={false} variant="rounded" width={120} height={12} />}
              >
                <Typography variant="caption">
                  <Skeleton animation={false} width="30%" />
                </Typography>
              </PortalAccessRow>
            )
          )}
        </Stack>
        {portalAccessError && (
          <Typography variant="caption" color="textSecondary" sx={{ px: 2.5, py: 1.5 }}>
            Some portal details could not be loaded, so they may be missing or out of date. Reload the page to try
            again.
          </Typography>
        )}
        <PortalAccessRelatedGuides guides={relatedGuides} />
      </Stack>
      {openDialog === "reset_password" && (
        <ResetPasswordDialog
          contactId={contact.id}
          contactEmail={contact.email}
          contactName={contact.name}
          onResetCompleted={handleResetCompleted}
          onClose={() => setOpenDialog(undefined)}
        />
      )}
      {openDialog === "resume_delivery" && (
        <ResumeDeliveryDialog
          contactId={contact.id}
          contactEmail={contact.email}
          onDeliveryResumed={handleDeliveryResumed}
          onClose={() => setOpenDialog(undefined)}
        />
      )}
      {openDialog === "change_email" && emailEditMode.kind === "dialog" && (
        <ContactEmailChangeDialog
          contact={contact}
          plan={emailEditMode.plan}
          onClose={() => setOpenDialog(undefined)}
          onSaved={handleEmailChanged}
        />
      )}
      <SendInvitationDialog
        open={openDialog === "send_invitation"}
        onClose={() => setOpenDialog(undefined)}
        onInvitationSent={handleInvitationSent}
        contactIds={[contact.id]}
        isResend={Boolean(contact.invitationSentAt)}
        description={
          <>
            You&apos;re about to send an invitation to <strong>{contact.name}</strong>.
          </>
        }
      />
    </EntitySection>
  );
};

export default ContactPortalAccessSection;
